import React, { useCallback, useEffect, useState } from 'react';
import { MESSAGE_WIDTH } from '../../constants';
import { Button, LeftOutlined, PlusOutlined, Spin, Tab } from '@fx-ui/fine-design';
import MessageSettingContent from './MessageSettingContent';
import { MessageReceiverTable, MsgTargetEditPane } from '../../components';
import { findIndex, first, get, isEmpty, SuiteId } from '@libs/shared';
import ResizeObserver from 'rc-resize-observer';
import classNames from 'classnames';
import { useCoinBalance, useMessageSettingTypes, useFetchReceiverMap } from '../../hooks';
import { MessageSettingContext } from '../../context';
import { getRequestUrl, hasFunctionCoin, post } from '../../../../utils';
import { authModel } from '../../../../models';
import { platformIntl, API } from '../../../../constants';

const { MIN_MESSAGE_TABLE_WIDTH } = MESSAGE_WIDTH;

interface MessageSettingProps {
    suiteId?: SuiteId;
}

/**
 * 通知管理
 *
 * @class
 */
const MessageSetting: React.FC<MessageSettingProps> = (props) => {
    const { suiteId } = props;
    const [settingType, setSettingType] = useState<string>();
    const [tableShow, setTableShow] = useState(false);
    const [addReceiverShow, setAddReceiverShow] = useState(false);
    const [receivers, setReceivers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { getCoinBalance, coinCount, smsPackCount } = useCoinBalance();
    const [editDefaultValue, setEditDefaultValue] = useState<Record<string, any> | null>(null);
    const [tableWidth, setTableWidth] = useState<number>(0);
    const { messageSettingTypes, fetchMessageSettingTypes } = useMessageSettingTypes();
    const { receiverMap, setReceiverMap, fetchReceiverMap } = useFetchReceiverMap();

    /**
     * 获取提醒对象
     */
    const getReceivers = () => {
        setLoading(true);
        post(getRequestUrl(API.message.list_receiver, authModel.tenantId)).then((res) => {
            setLoading(false);
            setReceivers(get(res, 'data.receivers', []));
        });
    };

    useEffect(() => {
        getCoinBalance();
        getReceivers();
        fetchMessageSettingTypes(suiteId);
        fetchReceiverMap();
    }, []);

    useEffect(() => {
        if (messageSettingTypes) {
            setSettingType(first(messageSettingTypes)?.key);
        }
    }, [messageSettingTypes]);

    /**
     * 获取表格部分头部
     */
    const getTableHeader = () => {
        return (
            <div className="message-setting-operation">
                <Button
                    compact
                    type="negative"
                    onClick={() => {
                        setTableShow(false);
                    }}
                    icon={<LeftOutlined />}
                >
                    {platformIntl.get('backTo')}
                </Button>
                <Button
                    compact
                    type="text"
                    onClick={() => {
                        setEditDefaultValue(null);
                        setAddReceiverShow(true);
                    }}
                    icon={<PlusOutlined />}
                >
                    {platformIntl.get('msg.target.add')}
                </Button>
            </div>
        );
    };

    /**
     * 添加提醒对象
     *
     * @param receiver 醒对象数据
     */
    const addOrUpdateReceiver = (receiver: any) => {
        if (!isEmpty(receiver)) {
            const newReceivers = [...receivers];
            const index = findIndex(newReceivers, (item) => item._id === receiver._id);
            if (index !== -1) {
                newReceivers[index] = receiver;
            } else {
                newReceivers.unshift(receiver);
            }
            setReceivers(newReceivers);
        }
    };

    /**
     * 提醒对象编辑回调
     */
    const handleReceiverEdit = useCallback((receiver: Record<string, any>) => {
        setEditDefaultValue(receiver);
        setAddReceiverShow(true);
    }, []);

    if (!settingType) {
        return <Spin />;
    }

    return (
        <MessageSettingContext.Provider
            value={{
                receiverMap: receiverMap ?? {},
                onReceiverMapChange: setReceiverMap,
                smsPackCount
            }}
        >
            <div className="fx-message-setting">
                <div className="message-setting-header">
                    {tableShow ? (
                        getTableHeader()
                    ) : (
                        <>
                            <div className="message-setting-operation">
                                <span>
                                    {hasFunctionCoin() &&
                                        platformIntl.getHTML(
                                            'fxp.base.workspace.msg.coin_balance.ttl',
                                            {
                                                coinCount: coinCount,
                                                smsCount: smsPackCount,
                                                CoinBalance: (s) => (
                                                    <span className="coin-balance-tip">{s}</span>
                                                ),
                                                MsgCount: (s) => (
                                                    <span className="msg-count-tip">{s}</span>
                                                )
                                            }
                                        )}
                                </span>
                                <Button
                                    compact
                                    onClick={() => {
                                        setTableShow(true);
                                    }}
                                >
                                    {platformIntl.get('msg.target.manage')}
                                </Button>
                            </div>
                            <Tab
                                activeKey={settingType}
                                className="message-setting-menu-top"
                                onTabActive={(key) => {
                                    setSettingType(key);
                                }}
                            >
                                {messageSettingTypes?.map((item) => (
                                    <Tab.Item
                                        tabClassName="message-setting-menu-item"
                                        tab={item.label}
                                        key={item.key}
                                    />
                                ))}
                            </Tab>
                        </>
                    )}
                </div>
                <ResizeObserver
                    onResize={({ width }) => {
                        if (tableWidth === 0) {
                            setTableWidth(Math.max(width, MIN_MESSAGE_TABLE_WIDTH));
                            return;
                        }
                        if (width !== tableWidth && width >= MIN_MESSAGE_TABLE_WIDTH) {
                            setTableWidth(width);
                        }
                    }}
                >
                    <div
                        className={classNames('message-setting-body', {
                            'show-receiver-table': tableShow
                        })}
                    >
                        {tableShow ? (
                            <>
                                {loading ? (
                                    <Spin />
                                ) : (
                                    <MessageReceiverTable
                                        receivers={receivers}
                                        onReceiverEdit={handleReceiverEdit}
                                        onReceiverChange={(value) => {
                                            setReceivers(value);
                                        }}
                                        tableWidth={tableWidth}
                                    />
                                )}
                            </>
                        ) : (
                            <MessageSettingContent type={settingType} tableWidth={tableWidth} />
                        )}
                    </div>
                </ResizeObserver>
                <MsgTargetEditPane
                    value={editDefaultValue}
                    visible={addReceiverShow}
                    onCancel={() => {
                        setAddReceiverShow(false);
                    }}
                    onOk={(receiver) => {
                        addOrUpdateReceiver(receiver);
                        setAddReceiverShow(false);
                    }}
                />
            </div>
        </MessageSettingContext.Provider>
    );
};

export default MessageSetting;
